import React, { useState } from 'react';
import api from '../../../services/api/axiosConfig'; // Correct path to your configured axios instance
import errorHandler from '../../../utils/errorHandler'; // Your global error handler utility
import { Spinner, Alert } from 'flowbite-react'; // Import Flowbite components for UI feedback
import { FaTimesCircle, FaCheckCircle } from 'react-icons/fa'; // Icons for alerts
import PasswordInput from '../../../components/PasswordInput'; // Import the new PasswordInput component
import Modal from '../../../components/Modal';

// Mock Label component (dependency) - Kept as is
const Label = ({ htmlFor, label }) => {
  return (
    <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-700 mb-1">
      {label}
    </label>
  );
};

// Mock ShowValidationMessage component (dependency) - Kept as is
const ShowValidationMessage = ({ error, id }) => {
  return (
    error ? <p id={id} className="mt-1 text-sm text-red-600">{error}</p> : null
  );
};

// The UpdatePasswordModal component
const UpdatePasswordModal = ({ isOpen, onClose , onUpdateSuccess}) => {
  const [formData, setFormData] = useState({
    current_password: '',
    password: '',
    password_confirmation: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false); // State for loading indicator
  const [responseError, setResponseError] = useState(null); // General API error message
  const [responseSuccess, setResponseSuccess] = useState(null); // General API success message

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' })); // Clear specific field error on change
    setResponseError(null); // Clear general response error on input change
    setResponseSuccess(null); // Clear general response success on input change
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({}); // Clear all previous field errors
    setResponseError(null); // Clear previous general error
    setResponseSuccess(null); // Clear previous success message

    // Client-side validation
    let newErrors = {};
    if (!formData.current_password) {
      newErrors.current_password = 'Current password is required.';
    }
    if (!formData.password) {
      newErrors.password = 'New password is required.';
    } else if (formData.password.length < 8) {
      newErrors.password = 'New password must be at least 8 characters.';
    }
    if (formData.password !== formData.password_confirmation) {
      newErrors.password_confirmation = 'Passwords do not match.';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true); // Start loading state

    try {
      // Make the API call to change password
      // Your axiosConfig handles 'Content-Type: application/json' by default
      const response = await api.post('/change-password', formData);

      // Based on your provided API response structure:
      // { "success": true, "message": "Password changed successfully" }
      if (response.data.success) {
        console.log('Password changed successfully:', response.data.message);
        setResponseSuccess(response.data.message); // Set general success message
        setFormData({ current_password: '', password: '', password_confirmation: '' }); // Clear form

        if (onUpdateSuccess) {
          onUpdateSuccess();
        }
      } else {
        // If API returns success: false but with a message
        setResponseError(response.data.message || 'Failed to change password.');
      }
    } catch (error) {
      console.error('Failed to change password:', error);
      setResponseSuccess(null); // Clear any lingering success message
      errorHandler(error,setErrors);

    } finally {
      setLoading(false); // Always stop loading, whether success or failure
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Change Password">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Display general response error alert */}
        {responseError && (
          <Alert color="failure" onDismiss={() => setResponseError(null)} className="mb-3">
            <FaTimesCircle className="mr-2" /> {responseError}
          </Alert>
        )}
        {/* Display general response success alert */}
        {responseSuccess && (
          <Alert color="success" onDismiss={() => setResponseSuccess(null)} className="mb-3">
            <FaCheckCircle className="mr-2" /> {responseSuccess}
          </Alert>
        )}

        <div>
          <Label htmlFor="current_password" label="Current Password" />
          <PasswordInput
            id="current_password"
            name="current_password"
            value={formData.current_password}
            onChange={handleChange}
            placeholder="••••••••"
            inputClasses={errors.current_password ? 'border-red-500 focus:ring-red-500' : ''} // Pass error styling via inputClasses
            disabled={loading} // Disable input while loading
          />
          <ShowValidationMessage error={errors.current_password} id="current-password-error" />
        </div>
        <div>
          <Label htmlFor="new_password" label="New Password" />
          <PasswordInput
            id="new_password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="••••••••"
            inputClasses={errors.password ? 'border-red-500 focus:ring-red-500' : ''} // Pass error styling via inputClasses
            disabled={loading} // Disable input while loading
          />
          <ShowValidationMessage error={errors.password} id="new-password-error" />
        </div>
        <div>
          <Label htmlFor="password_confirmation" label="Confirm New Password" />
          <PasswordInput
            id="password_confirmation"
            name="password_confirmation"
            value={formData.password_confirmation}
            onChange={handleChange}
            placeholder="••••••••"
            inputClasses={errors.password_confirmation ? 'border-red-500 focus:ring-red-500' : ''} // Pass error styling via inputClasses
            disabled={loading} // Disable input while loading
          />
          <ShowValidationMessage error={errors.password_confirmation} id="confirm-password-error" />
        </div>
        <button
          type="submit"
          className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={loading} // Disable button when loading
        >
          {loading ? (
            <>
              <Spinner size="sm" className="mr-2" />
              Changing...
            </>
          ) : 'Change Password'}
        </button>
      </form>
    </Modal>
  );
};

export default UpdatePasswordModal;
